import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  DispatchWithoutAction,
} from "react";
import getConfig from "next/config";
import { getSelectorsByUserAgent } from "react-device-detect";
import { UserAgentSelectors, useUserAgent } from "common/UserAgent";
import { useStore } from "store";
import { useApi } from "services/ApiService";
import { checkDisabledReviews } from "helpers/reviews";
import { getExperimentCondition } from "helpers/cookies";
import { getImageSrcWithSize, originImage } from "helpers/image";
import { gtm } from "helpers/analytics";
import { DESKTOP, LAPTOP, TABLET } from "constants/mixins";
import {
  DEFAULT_REGION_CODE,
  HEADER_LAPTOP_HEIGHT_STICKY,
  HEADER_MOBILE_HEIGHT_STICKY,
  HEADER_TABLET_HEIGHT_STICKY,
} from "constants/variables";
import {
  EntityInput,
  getEntityAs,
  getEntityHref,
  isURLAbsolute,
  LinkProps,
  LinkPropsOptions,
  withTrailSlash,
} from "./url";

const { publicRuntimeConfig } = getConfig();

export const useDevice = (): Partial<UserAgentSelectors> => {
  return getSelectorsByUserAgent(useUserAgent());
};

export type UseMounted = {
  isMounted: () => boolean;
};

/**
 * @deprecated
 */
export const useMounted = (): UseMounted => {
  const mounted = useRef<boolean>(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);
  const isMounted = () => {
    return mounted.current;
  };
  return { isMounted };
};

export type Breakpoint = "" | "mobile" | "tablet" | "laptop" | "desktop";
export const useBreakpoint = (ssr?: boolean): Breakpoint => {
  const device = useDevice();
  const deviceBreakpoint =
    device.isMobile && !device.isTablet
      ? "mobile"
      : device.isTablet
      ? "tablet"
      : "desktop";
  const initialBreakpoint = ssr ? deviceBreakpoint : "";
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(initialBreakpoint);

  useEffect(() => {
    function updateBreakpoint(): () => void {
      if (typeof window === "undefined") {
        return;
      }

      /* todo: rework with window.matchMedia */
      const isTablet =
        window.innerWidth < LAPTOP && window.innerWidth >= TABLET;
      const isLaptop =
        window.innerWidth < DESKTOP && window.innerWidth >= LAPTOP;
      const isDesktop = window.innerWidth >= DESKTOP;
      let value = "mobile";

      if (isTablet) {
        value = "tablet";
      }

      if (isLaptop) {
        value = "laptop";
      }

      if (isDesktop) {
        value = "desktop";
      }

      breakpoint !== value && setBreakpoint(value as Breakpoint);
    }

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);

    return () => {
      window.removeEventListener("resize", updateBreakpoint);
    };
  }, [breakpoint]);

  return breakpoint;
};

/* todo: use types */
export const useScrollToTarget = () => {
  const breakpoint = useBreakpoint();
  const headerHeight = useMemo(() => {
    switch (breakpoint) {
      case "mobile":
        return HEADER_MOBILE_HEIGHT_STICKY;
      case "tablet":
        return HEADER_TABLET_HEIGHT_STICKY;
      case "laptop":
      default:
        return HEADER_LAPTOP_HEIGHT_STICKY;
    }
  }, [breakpoint]);

  const scrollToTarget = target => {
    if (!target) {
      return;
    }

    window.scrollTo({
      top: target.offsetTop - Number(headerHeight.replace("px", "")),
      behavior: "smooth",
    });
  };
  return scrollToTarget;
};

/* todo: refactor */
export const useForceUpdate = (): DispatchWithoutAction => {
  return useReducer(() => ({}), {})[1];
};

export type UseUrl = {
  getEntityLinkProps: (
    type: string,
    entity?: EntityInput,
    options?: Partial<LinkPropsOptions>
  ) => LinkProps;
  getLinkProps: (
    url: string,
    pathname?: string,
    options?: Partial<LinkPropsOptions>
  ) => LinkProps;
};

/* todo: refactor, memoize result linkProps object */
export const useUrl = (): UseUrl => {
  const { location } = useStore();
  const origin = publicRuntimeConfig.ORIGIN_HOST || "";

  const regionSegment =
    location.region &&
    String(location.regionID).toLowerCase() !==
      DEFAULT_REGION_CODE.toLowerCase()
      ? `${location.regionID}`
      : "";

  /* todo: move pathname to options ? */
  const getLinkProps = (
    url: string,
    pathname?: string,
    options?: Partial<LinkPropsOptions>
  ): LinkProps => {
    const opts = {
      withOrigin: false,
      withRegion: true,
      ...(options || {}),
    };
    const obj: LinkProps = {
      href: { pathname: pathname || url },
      as: url,
      prefetch: false,
    };

    if (isURLAbsolute(url)) {
      return { href: url, as: url };
    }

    if (opts.withRegion && regionSegment) {
      obj.href["query"] = { region: regionSegment };
      obj.as = `${withTrailSlash(String(obj.as))}r_${regionSegment}/`;
    }

    if (opts.withOrigin && origin) {
      obj.as = obj.href = origin + obj.as;
    }
    return obj;
  };

  const getEntityLinkProps = (
    type: string,
    entity: EntityInput,
    options?: LinkPropsOptions
  ): LinkProps => {
    const opts = {
      withOrigin: true,
      withRegion: true,
      ...(options || {}),
    };

    const href = getEntityHref.call(this, type, entity, {
      origin: opts.withOrigin ? origin : undefined,
      regionSegment: opts.withRegion ? regionSegment : undefined,
    });
    const as = getEntityAs.call(this, type, entity, {
      origin: opts.withOrigin ? origin : undefined,
      regionSegment: opts.withRegion ? regionSegment : undefined,
    });
    return { href, as, prefetch: false };
  };

  return {
    getEntityLinkProps: useCallback(getEntityLinkProps, [
      origin,
      regionSegment,
    ]),
    getLinkProps: useCallback(getLinkProps, [origin, regionSegment]),
  };
};

export const useCheckDisabledReviews = (product: Entity.Product): boolean => {
  return useMemo(() => {
    return checkDisabledReviews(product);
  }, [product]);
};

export const useIsTouchDevice = () => {
  if (typeof window === "undefined") return false;

  return (
    "ontouchstart" in window || (navigator as Navigator).maxTouchPoints > 0
  );
};

export const useEventListenerOptions = (
  options: AddEventListenerOptions = {}
): AddEventListenerOptions | boolean => {
  const supportsOptions = useRef(null);

  if (typeof window === "undefined") return false;

  if (supportsOptions.current === null) {
    try {
      const opts = Object.defineProperty({}, "passive", {
        get: function () {
          supportsOptions.current = true;
        },
      });
      window.addEventListener("testPassive", null, opts);
      window.removeEventListener("testPassive", null, opts);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  return supportsOptions.current ? options : !!options.capture;
};

export const useLunaTrack = ({
  product,
  analyticsMeta,
}: {
  product: Entity.Product;
  analyticsMeta?: Entity.ProductAnalyticsMeta;
}): {
  onImps: () => Promise<void>;
  onTrack: (event?: React.MouseEvent) => Promise<void>;
  hasImps: boolean;
  hasTracks: boolean;
} => {
  const tracked = useRef(false);
  const counters = product?.counters?.advertisement;

  const hasImps = useMemo((): boolean => {
    return !!counters?.imps?.length;
  }, [counters]);

  const hasTracks = useMemo((): boolean => {
    return !!counters?.track?.length;
  }, [counters]);

  const onImps = useCallback(async (): Promise<void> => {
    if (!hasImps) return;

    try {
      gtm.onLunaProductWatch(product, analyticsMeta);

      await Promise.all(
        counters?.imps.map(imp => {
          return fetch(imp);
        })
      );
    } catch (err) {
      console.error(err);
    }
  }, [counters]);

  const onTrack = useCallback(
    async (event?: React.MouseEvent): Promise<void> => {
      if (tracked.current || !hasTracks) return;

      tracked.current = true;

      const itemHasBeenAdded = !event;
      const itemHasBeenViewed =
        event &&
        ([0, 1].includes(event.button) ||
          ((event.ctrlKey || event.shiftKey || event.metaKey) &&
            event.button === 0));

      if (itemHasBeenViewed || itemHasBeenAdded) {
        gtm.onLunaProductClick(product, analyticsMeta);

        try {
          await Promise.all(
            counters?.track.map(track => {
              return fetch(track);
            })
          );
        } catch (err) {
          console.error(err);
        }
      }
    },
    [counters]
  );

  return {
    onImps,
    onTrack,
    hasImps,
    hasTracks,
  };
};

export const useExperiment = (
  name: string,
  strict = true
): Common.UnionCookie => {
  const [value, setValue] = useState<Common.UnionCookie>(false);

  useEffect(() => {
    setValue(getExperimentCondition(name, strict));
  }, [name, strict]);

  return value;
};

export const usePageLoaded = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onPageLoad = () => {
      setLoaded(true);
    };

    if (document.readyState === "complete") {
      onPageLoad();
    } else {
      window.addEventListener("load", onPageLoad);

      return () => window.removeEventListener("load", onPageLoad);
    }
  }, []);

  return loaded;
};

export const useWidget = (variables: Array<Api.Widget.ItemInput>) => {
  const [widgets, setWidgets] = useState(
    new Map<Entity.Widget.Alias, Array<Entity.Product>>()
  );

  const [isLoading, setIsLoading] = useState(true);
  const { location, user } = useStore();
  const { methods } = useApi();

  const submittingRef = useRef(false);

  useEffect(() => {
    const fetch = async () => {
      if (submittingRef.current) {
        return;
      }

      const items = variables.filter(Boolean);
      if (!items?.length || user.isEmployeeMode) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      submittingRef.current = true;

      const { data, errors } = await methods.widgets.get({
        input: {
          items,
          regionID: location.regionID,
        },
      });

      if (data?.widgets?.items) {
        const widgetMap = new Map();
        data.widgets.items.map(item => {
          widgetMap.set(item.alias, item.items);
        });

        setWidgets(widgetMap);
      }

      setIsLoading(false);
      submittingRef.current = false;

      if (errors) {
        console.error("api.methods.widget.get errors:", errors);
      }
    };

    fetch();

    return () => {
      if (widgets.size) widgets.clear();
    };
    // other values are not needed, they are re-created when widgets are requested
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.regionID, variables, user.isEmployeeMode]);

  return { widgets, isLoading };
};

export const useProductPrices = (
  product: Entity.Product,
  // игнорировать фильтрацию ИСГ прайса или нет.
  showExpiringPrices?: boolean
) => {
  const { user } = useStore();

  return useMemo(() => {
    const expiringPrice = showExpiringPrices
      ? product?.prices?.find(price => price.isExpirating && price.price > 0)
      : false;

    if (expiringPrice) {
      if (
        !product.price ||
        (!product.availableForBooking && !product.availableForOrder)
      ) {
        return [expiringPrice];
      }

      return [
        {
          dateExpired: product.expirationDate,
          discount: product.discount,
          maxQuantity: product.maxQuantity,
          price: product.price,
          priceOld: product.priceOld,
          priceTypeID: product.priceTypeID,
        },
        expiringPrice,
      ];
    }

    if (!user.isEmployeeMode) {
      const currentPrice = product?.prices?.find(
        item => item.priceTypeID === product?.priceTypeID
      );

      return [
        {
          maxQuantity: product?.maxQuantity,
          price: product?.price,
          priceOld: product?.priceOld,
          priceTypeID: product?.priceTypeID,
          discount: product?.discount,
          dateExpired: product?.expirationDate,
          dateExpiredOpened: currentPrice?.dateExpiredOpened,
          hasPrefix: currentPrice?.hasPrefix,
        },
      ];
    }

    if (user.isEmployeeMode)
      return product?.prices?.filter(price => price.price > 0) || [];
  }, [product, user.isEmployeeMode, showExpiringPrices]);
};

export const useImage = (
  src: string,
  sizes?: Partial<{
    mobile: string;
    tablet: string;
    laptop: string;
    unknown: string;
  }>
): string => {
  const breakpoint = useBreakpoint(true);
  const { mobile, tablet, laptop, unknown } = sizes;

  return useMemo(() => {
    switch (breakpoint) {
      case "mobile":
        return getImageSrcWithSize(src, mobile);
      case "tablet":
        return getImageSrcWithSize(src, tablet || mobile);
      case "laptop":
      case "desktop":
        return getImageSrcWithSize(src, laptop || tablet || mobile);
      case "":
        return unknown ? getImageSrcWithSize(src, unknown) : originImage(src);
      default:
        return originImage(src);
    }
  }, [breakpoint, src, mobile, tablet, laptop, unknown]);
};

export const useMountTransition = (
  isMounted: boolean,
  timeout: number
): boolean => {
  const [transitionIn, setTransitionIn] = useState<boolean>(false);
  const timeoutId = useRef(null);

  useEffect(() => {
    if (isMounted && !transitionIn) {
      setTransitionIn(true);
    }

    if (!isMounted && transitionIn) {
      timeoutId.current = setTimeout(() => setTransitionIn(false), timeout);
    }

    return () => {
      clearTimeout(timeoutId.current);
    };
  }, [timeout, isMounted]);

  return transitionIn;
};

export const useTooltipComputedStyles = ({ rect, placement, maxWidth }) => {
  return useMemo(() => {
    if (!rect) return {};

    const DEFAULT_TOOLTIP_INDENT = 10;
    let styles = {};
    let computedPlacement = placement;

    if (
      ["top-end", "top-start", "bottom-start", "bottom-end"].includes(placement)
    ) {
      const placementPath = placement.split("-")[0] as "top" | "bottom";
      const offsetLeft = rect.left + rect.width - maxWidth;
      const offsetRight =
        document.documentElement.clientWidth - rect.left - maxWidth;

      computedPlacement =
        offsetLeft < 0 || offsetRight < 0
          ? offsetLeft > offsetRight
            ? `${placementPath}-end`
            : `${placementPath}-start`
          : placement;
    }

    switch (computedPlacement) {
      case "top-start":
        styles = {
          left: rect.left + "px",
          bottom: `${
            document.documentElement.clientHeight - rect.top - window.scrollY
          }px`,
          paddingBottom: DEFAULT_TOOLTIP_INDENT + "px",
        };
        break;
      case "top":
        styles = {
          left: `${rect.left + rect.width / 2}px`,
          bottom: `${
            document.documentElement.clientHeight - rect.top - window.scrollY
          }px`,
          transform: "translateX(-50%)",
          paddingBottom: DEFAULT_TOOLTIP_INDENT + "px",
        };
        break;
      case "top-end":
        styles = {
          right: `${
            document.documentElement.clientWidth - rect.left - rect.width
          }px`,
          bottom: `${
            document.documentElement.clientHeight - rect.top - window.scrollY
          }px`,
          paddingBottom: DEFAULT_TOOLTIP_INDENT + "px",
        };
        break;
      case "right-start":
        styles = {
          left: `${rect.left + rect.width + DEFAULT_TOOLTIP_INDENT}px`,
          top: `${rect.top + window.scrollY}px`,
        };
        break;
      case "right":
        styles = {
          left: `${rect.left + rect.width + DEFAULT_TOOLTIP_INDENT}px`,
          top: `${rect.top + rect.height / 2 + window.scrollY}px`,
          transform: "translateY(-50%)",
        };
        break;
      case "right-end":
        styles = {
          left: `${rect.left + rect.width + DEFAULT_TOOLTIP_INDENT}px`,
          bottom: `${
            document.documentElement.clientHeight - rect.bottom - window.scrollY
          }px`,
        };
        break;
      case "bottom-end":
        styles = {
          right: `${
            document.documentElement.clientWidth - rect.left - rect.width
          }px`,
          top: `${rect.bottom - 1 + window.scrollY}px`,
          paddingTop: DEFAULT_TOOLTIP_INDENT + "px",
        };
        break;
      case "bottom":
        styles = {
          left: `${rect.left + rect.width / 2}px`,
          top: `${rect.bottom - 1 + window.scrollY}px`,
          paddingTop: DEFAULT_TOOLTIP_INDENT + "px",
          transform: "translateX(-50%)",
        };
        break;
      case "bottom-start":
        styles = {
          left: `${rect.left}px`,
          top: `${rect.bottom - 1 + window.scrollY}px`,
          paddingTop: DEFAULT_TOOLTIP_INDENT + "px",
        };
        break;
      case "left-end":
        styles = {
          right: `${
            document.documentElement.clientWidth -
            rect.right +
            rect.width +
            DEFAULT_TOOLTIP_INDENT
          }px`,
          bottom: `${
            document.documentElement.clientHeight - rect.bottom - window.scrollY
          }px`,
        };
        break;
      case "left":
        styles = {
          right: `${
            document.documentElement.clientWidth -
            rect.right +
            rect.width +
            DEFAULT_TOOLTIP_INDENT
          }px`,
          top: `${rect.top + rect.height / 2 + window.scrollY}px`,
          transform: "translateY(-50%)",
        };
        break;
      case "left-start":
        styles = {
          right: `${
            document.documentElement.clientWidth -
            rect.right +
            rect.width +
            DEFAULT_TOOLTIP_INDENT
          }px`,
          top: `${rect.top + window.scrollY}px`,
        };
        break;
    }

    return styles;
  }, [rect, placement]);
};
